import { useState, useEffect } from 'react'
import { Trash2 } from 'lucide-react'
import { syncManager } from '../services/SyncManager'

/**
 * SyncStatusBar — floating indicator showing real-time sync status.
 *
 * States:
 *   ● Online — all data synced
 *   ● Syncing — pushing/pulling data
 *   ● Offline — N changes queued
 *   ⚠ Pending — online but has queued ops
 */
/** `/api/v1/partner-crm/touches` из полного адреса — человеку так понятнее. */
function shortPath(url) {
    try {
        return new URL(url).pathname
    } catch {
        return url
    }
}

export default function SyncStatusBar() {
    const [syncState, setSyncState] = useState({
        isOnline: navigator.onLine,
        pendingCount: syncManager.pendingCount,
        droppedCount: syncManager.droppedCount,
        droppedOps: syncManager.droppedOps,
        isFlushing: false,
        status: syncManager.status,
    })

    const [showDetails, setShowDetails] = useState(false)
    const [lastOnlineTime, setLastOnlineTime] = useState(Date.now())
    const [animate, setAnimate] = useState(false)

    useEffect(() => {
        const unsub = syncManager.subscribe((state) => {
            setSyncState(state)
            if (state.isOnline && !syncState.isOnline) {
                setLastOnlineTime(Date.now())
                setAnimate(true)
                setTimeout(() => setAnimate(false), 2000)
            }
        })
        return unsub
    }, [syncState.isOnline])

    const STATUS_CONFIG = {
        synced: {
            color: 'var(--color-success-text)',
            bgColor: 'color-mix(in srgb, var(--color-success) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-success)',
            icon: '●',
            label: 'Синхронизировано',
            pulse: false,
        },
        syncing: {
            color: 'var(--color-warning-text)',
            bgColor: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-warning)',
            icon: '↻',
            label: 'Синхронизация...',
            pulse: true,
        },
        offline: {
            color: 'var(--color-danger-text)',
            bgColor: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-danger)',
            icon: '●',
            label: 'Офлайн',
            pulse: false,
        },
        pending: {
            color: 'var(--color-warning-text)',
            bgColor: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-warning)',
            icon: '...',
            label: 'Ожидание синхронизации',
            pulse: true,
        },
        error: {
            color: 'var(--color-danger-text)',
            bgColor: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-danger)',
            icon: '⚠',
            label: 'Ошибка синхронизации',
            pulse: false,
        },
        // Отложенная отправка не доехала и уже не доедет. Молчать нельзя:
        // человеку было сказано «сохранено и отправится само», а «Синхронизировано»
        // с нулём в очереди прочиталось бы как подтверждение — и правка исчезла бы
        // тихо. Показываем прямо и предлагаем повторить действие вручную.
        failed: {
            color: 'var(--color-danger-text)',
            bgColor: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-bg-elevated))',
            borderColor: 'var(--color-danger)',
            icon: '⚠',
            label: 'Часть изменений не отправлена',
            pulse: false,
        },
    }

    const config = STATUS_CONFIG[syncState.status] || STATUS_CONFIG.synced

    // Auto-hide when synced after 5 seconds
    const [visible, setVisible] = useState(true)
    useEffect(() => {
        if (syncState.status === 'synced') {
            const t = setTimeout(() => setVisible(false), 5000)
            return () => clearTimeout(t)
        } else {
            setVisible(true)
        }
    }, [syncState.status])

    // Always show on hover / when not synced
    const shouldShow = visible || syncState.status !== 'synced'

    return (
        <>
            <style>{`
                @keyframes sync-pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
                @keyframes sync-slide-in {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .sync-status-bar {
                    position: fixed;
                    bottom: 16px;
                    right: 16px;
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 14px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    backdrop-filter: blur(12px);
                    cursor: pointer;
                    user-select: none;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    animation: sync-slide-in 0.3s ease-out;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
                }
                .sync-status-bar:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
                }
                .sync-status-bar--hidden {
                    opacity: 0;
                    transform: translateY(10px);
                    pointer-events: none;
                }
                .sync-pulse {
                    animation: sync-pulse 1.5s ease-in-out infinite;
                }
                .sync-details {
                    position: fixed;
                    bottom: 56px;
                    right: 16px;
                    z-index: 9998;
                    width: 280px;
                    padding: 16px;
                    border-radius: 16px;
                    background: var(--color-bg-card);
                    border: 1px solid var(--color-border);
                    backdrop-filter: blur(16px);
                    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                    animation: sync-slide-in 0.2s ease-out;
                }
            `}</style>
            {/* Main indicator */}
            <div
                className={`sync-status-bar ${!shouldShow ? 'sync-status-bar--hidden' : ''} ${config.pulse ? 'sync-pulse' : ''}`}
                style={{
                    background: config.bgColor,
                    border: `1px solid ${config.borderColor}`,
                    color: config.color,
                }}
                onClick={() => setShowDetails(!showDetails)}
                onMouseEnter={() => setVisible(true)}
            >
                <span className="text-lg">{config.icon}</span>
                <span>{config.label}</span>
                {syncState.pendingCount > 0 && (
                    <span
                        style={{
                            padding: '2px 7px',
                            background: config.color,
                            minWidth: 18
                        }}
                        className='radius-full text-xxs fw-800 color-white text-center'>
                        {syncState.pendingCount}
                    </span>
                )}
            </div>
            {/* Details popup */}
            {showDetails && (
                <div className="sync-details" onClick={() => setShowDetails(false)}>
                    <div className='text-lg fw-700 mb-12 flex items-center gap-6'>
                        {config.icon} Статус синхронизации
                    </div>

                    <div className="col-gap-8">
                        <div className='flex justify-between text-xs'>
                            <span className="text-muted">Соединение</span>
                            <span
                                style={{
                                    color: syncState.isOnline ? 'var(--color-success)' : 'var(--color-danger)'
                                }}
                                className='fw-600'>
                                {syncState.isOnline ? '● Подключено' : '● Нет связи'}
                            </span>
                        </div>

                        <div className='flex justify-between text-xs'>
                            <span className="text-muted">В очереди</span>
                            <span className="fw-600">
                                {syncState.pendingCount === 0 ? '✓ Нет' : `${syncState.pendingCount} изменений`}
                            </span>
                        </div>

                        <div className='flex justify-between text-xs'>
                            <span className="text-muted">Статус</span>
                            <span
                                style={{
                                    color: config.color
                                }}
                                className='fw-600'>
                                {config.label}
                            </span>
                        </div>

                        <div className='flex justify-between text-xs'>
                            <span className="text-muted">Offline-кэш</span>
                            <span className="fw-600 color-blue">
                                {'serviceWorker' in navigator ? '✓ PWA активен' : '✗ Нет SW'}
                            </span>
                        </div>
                    </div>

                    {/* Что именно не доехало. Без списка и без кнопки человек
                        видел тревогу без предмета и без действия: плашка висела
                        до перезагрузки и навсегда заслоняла «Синхронизировано». */}
                    {syncState.droppedCount > 0 && (
                        <div className="mt-12">
                            <div className="text-xs fw-700 text-danger">
                                Не отправлено: {syncState.droppedCount}
                            </div>
                            <div className="text-xs text-muted mt-6">
                                Эти изменения придётся внести заново — откройте нужный экран и повторите действие.
                            </div>
                            <ul className="text-xs text-muted mt-6" style={{ paddingLeft: 16, margin: 0 }}>
                                {(syncState.droppedOps || []).slice(0, 5).map((op, i) => (
                                    <li key={`${op.url}-${i}`}>
                                        {op.method} {shortPath(op.url)} — {op.reason}
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={(e) => { e.stopPropagation(); syncManager.clearDropped() }}
                                style={{ padding: '6px 16px', border: '1px solid var(--color-border)', background: 'transparent' }}
                                className='mt-6 w-full radius-8 text-muted text-xs cursor-pointer'>
                                Понятно
                            </button>
                        </div>
                    )}

                    {syncState.pendingCount > 0 && syncState.isOnline && (
                        <button
                            onClick={(e) => { e.stopPropagation(); syncManager.flushQueue() }}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent))',
                                transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                            className='mt-12 w-full radius-8 color-white text-sm fw-700 cursor-pointer'>
                            ↻ Синхронизировать сейчас
                        </button>
                    )}

                    {syncState.pendingCount > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); syncManager.clearQueue() }}
                            style={{
                                padding: '6px 16px',
                                border: '1px solid var(--color-border)',
                                background: 'transparent'
                            }}
                            className='mt-6 w-full radius-8 text-muted text-xs cursor-pointer'>
                            <><Trash2 size={14} />{" "}Очистить очередь</>
                        </button>
                    )}

                    <div className='text-xxxs'>
                        Изменения автоматически синхронизируются при подключении к интернету
                    </div>
                </div>
            )}
        </>
    );
}

